/* eslint-disable no-unused-expressions */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnamesBind from 'classnames/bind';
import shortId from '@concur/nui-widgets/lib/utils/shortId';
import Overlay from './_Overlay';

const MenuItemToggle = React.forwardRef((props, ref) => {
    const {
        buttonAriaLabel,
        buttonClassName,
        buttonProps,
        caretClassName,
        children,
        className,
        classNameMap,
        content,
        cssBlock,
        hideCaret,
        onClick,
        onClose,
        overlayContentClassName,
        overlayProps,
        popperPlacement,
        includeTargetInTabFlow,
    } = props;

    const classnames = classnamesBind.bind(classNameMap);

    const [expanded, setExpanded] = useState(false);

    const buttonId = shortId.generate();

    const handleToggleClick = (e) => {
        setExpanded(!expanded);
        onClick(e, !expanded);
    };

    const handleClose = () => {
        setExpanded(false);
        onClose();
        document.getElementById(buttonId)?.focus();
    };

    const classes = classnames(`${cssBlock}__listitem`, className);

    const buttonClasses = classnames(
        `${cssBlock}__button`,
        buttonClassName,
        {
            [`${cssBlock}__button--active`]: expanded,
        },
    );

    return (
        <li className={classes} ref={ref}>
            <Overlay
                {...overlayProps}
                content={React.Children.map(children, (child) => React.cloneElement(child, {
                    onClose: handleClose,
                }))}
                disableEdgeDetection
                onHide={handleClose}
                contentClassName={overlayContentClassName}
                placement={popperPlacement}
                show={expanded}
                targetClassName={classnames(`${cssBlock}__target`)}
                includeTargetInTabFlow={includeTargetInTabFlow}
            >
                <button
                    {...buttonProps}
                    aria-expanded={expanded}
                    aria-haspopup="dialog"
                    aria-label={buttonAriaLabel}
                    className={buttonClasses}
                    id={buttonId}
                    onClick={handleToggleClick}
                    type="button"
                >
                    {content}
                    {!hideCaret && <i aria-hidden className={classnames(`${cssBlock}__caret`, caretClassName)} />}
                </button>
            </Overlay>
        </li>
    );
});

MenuItemToggle.displayName = 'MenuItemToggle';

MenuItemToggle.propTypes = {
    buttonAriaLabel: PropTypes.string,
    buttonClassName: PropTypes.string,
    buttonProps: PropTypes.object,
    caretClassName: PropTypes.string,
    children: PropTypes.node,
    className: PropTypes.string,
    content: PropTypes.node,
    onClick: PropTypes.func,
    onClose: PropTypes.func,
    overlayContentClassName: PropTypes.string,
    overlayProps: PropTypes.object,
    cssBlock: PropTypes.string,
    hideCaret: PropTypes.bool,
    popperPlacement: PropTypes.string,
    includeTargetInTabFlow: PropTypes.bool,
};

MenuItemToggle.defaultProps = {
    onClick: () => {},
    onClose: () => {},
    popperPlacement: 'bottom-start',
};

export default MenuItemToggle;
